import { useState, useEffect } from 'react'
import {
  TextInput, Textarea, Button, Title, Stack, Group, Text, Divider,
  ActionIcon, Box, Card, SimpleGrid,
} from '@mantine/core'
import { useForm } from '@mantine/form'
import { notifications } from '@mantine/notifications'
import {
  IconPlus, IconTrash, IconArrowUp, IconArrowDown, IconArrowLeft,
  IconPhoto, IconStar, IconBook, IconVideo, IconSchool, IconDiamond,
  IconArrowRight, IconQuestionMark, IconLayout,
} from '@tabler/icons-react'
import { api } from '@/lib/api'
import ImageUpload from '@/components/ImageUpload'

interface FAQ {
  question: string
  answer: string
}

interface HeroSlide {
  title: string
  subtitle: string
  buttonText: string
  buttonLink: string
  imageUrl: string
  order: number
}

interface HomeConfigValues {
  heroSlides: HeroSlide[]
  hero: { badge: string; title: string; subtitle: string }
  featuredSection: { title: string; subtitle: string }
  coursesSection: { title: string; subtitle: string }
  videosSection: { title: string; subtitle: string }
  teachersSection: { title: string; subtitle: string }
  premiumCta: { title: string; subtitle: string; buttonText: string }
  bottomCta: { title: string; subtitle: string; buttonText: string }
  faqs: FAQ[]
}

type SectionKey = keyof HomeConfigValues

const sections: { key: SectionKey; label: string; description: string; icon: typeof IconPhoto; color: string }[] = [
  { key: 'heroSlides', label: 'Hero Slides', description: 'Нүүр хуудасны гол слайд зурагнууд', icon: IconPhoto, color: 'violet' },
  { key: 'hero', label: 'Hero хэсэг', description: 'Badge, гарчиг, дэд гарчиг', icon: IconLayout, color: 'blue' },
  { key: 'featuredSection', label: 'Онцлох хэсэг', description: 'Онцлох контентын гарчиг', icon: IconStar, color: 'yellow' },
  { key: 'coursesSection', label: 'Сургалтын хэсэг', description: 'Сургалтын жагсаалтын гарчиг', icon: IconBook, color: 'green' },
  { key: 'videosSection', label: 'Видео хэсэг', description: 'Видео жагсаалтын гарчиг', icon: IconVideo, color: 'red' },
  { key: 'teachersSection', label: 'Багш нарын хэсэг', description: 'Багш нарын жагсаалтын гарчиг', icon: IconSchool, color: 'cyan' },
  { key: 'premiumCta', label: 'Premium CTA', description: 'Премиум эрх авах', icon: IconDiamond, color: 'grape' },
  { key: 'bottomCta', label: 'Доод CTA', description: 'Апп татах', icon: IconArrowRight, color: 'blue' },
  { key: 'faqs', label: 'Түгээмэл асуултууд', description: 'FAQ асуулт хариулт', icon: IconQuestionMark, color: 'teal' },
]

export default function HomeConfig() {
  const [loading, setLoading] = useState(false)
  const [initialLoading, setInitialLoading] = useState(true)
  const [activeSection, setActiveSection] = useState<SectionKey | null>(null)

  const form = useForm<HomeConfigValues>({
    initialValues: {
      heroSlides: [],
      hero: { badge: '', title: '', subtitle: '' },
      featuredSection: { title: '', subtitle: '' },
      coursesSection: { title: '', subtitle: '' },
      videosSection: { title: '', subtitle: '' },
      teachersSection: { title: '', subtitle: '' },
      premiumCta: { title: '', subtitle: '', buttonText: '' },
      bottomCta: { title: '', subtitle: '', buttonText: '' },
      faqs: [],
    },
  })

  useEffect(() => {
    api.admin.getHomeConfig().then((config) => {
      const c = config as unknown as HomeConfigValues
      form.setValues({
        heroSlides: c.heroSlides || [],
        hero: c.hero || { badge: '', title: '', subtitle: '' },
        featuredSection: c.featuredSection || { title: '', subtitle: '' },
        coursesSection: c.coursesSection || { title: '', subtitle: '' },
        videosSection: c.videosSection || { title: '', subtitle: '' },
        teachersSection: c.teachersSection || { title: '', subtitle: '' },
        premiumCta: c.premiumCta || { title: '', subtitle: '', buttonText: '' },
        bottomCta: c.bottomCta || { title: '', subtitle: '', buttonText: '' },
        faqs: c.faqs || [],
      })
      setInitialLoading(false)
    }).catch(() => setInitialLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleSave = async () => {
    setLoading(true)
    try {
      await api.admin.updateHomeConfig(form.values as unknown as Record<string, unknown>)
      notifications.show({ title: 'Амжилттай', message: 'Тохиргоо хадгалагдлаа', color: 'green' })
    } catch {
      notifications.show({ title: 'Алдаа', message: 'Хадгалахад алдаа гарлаа', color: 'red' })
    } finally {
      setLoading(false)
    }
  }

  const addHeroSlide = () => {
    form.insertListItem('heroSlides', {
      title: '', subtitle: '', buttonText: '', buttonLink: '', imageUrl: '',
      order: form.values.heroSlides.length,
    })
  }

  const removeHeroSlide = (index: number) => form.removeListItem('heroSlides', index)

  const moveHeroSlide = (index: number, direction: 'up' | 'down') => {
    const newIndex = direction === 'up' ? index - 1 : index + 1
    if (newIndex < 0 || newIndex >= form.values.heroSlides.length) return
    form.reorderListItem('heroSlides', { from: index, to: newIndex })
  }

  const addFaq = () => form.insertListItem('faqs', { question: '', answer: '' })
  const removeFaq = (index: number) => form.removeListItem('faqs', index)

  if (initialLoading) return null

  const activeMeta = sections.find((s) => s.key === activeSection)

  // Section editor
  if (activeSection) {
    return (
      <div>
        <Group mb="lg">
          <ActionIcon variant="light" radius="md" size="lg" onClick={() => setActiveSection(null)}>
            <IconArrowLeft size={20} />
          </ActionIcon>
          <div>
            <Title order={2}>{activeMeta?.label}</Title>
            <Text size="sm" c="dimmed">{activeMeta?.description}</Text>
          </div>
        </Group>

        <Card shadow="xs" radius="md" withBorder padding="lg" maw={700}>
          {/* Hero Slides */}
          {activeSection === 'heroSlides' && (
            <Stack gap="md">
              <Group justify="space-between">
                <Text fw={600}>Slides ({form.values.heroSlides.length})</Text>
                <Button leftSection={<IconPlus size={16} />} variant="light" size="xs" onClick={addHeroSlide}>
                  Нэмэх
                </Button>
              </Group>

              {form.values.heroSlides.length === 0 && (
                <Text size="sm" c="dimmed" ta="center" py="md">Hero slide байхгүй байна</Text>
              )}

              {form.values.heroSlides.map((_slide, index) => (
                <Card key={index} withBorder radius="md" padding="md" bg="var(--mantine-color-gray-0)">
                  <Group align="flex-start" gap="sm">
                    <Stack gap="xs" style={{ flex: 1 }}>
                      <TextInput label={`Slide ${index + 1} — Гарчиг`} {...form.getInputProps(`heroSlides.${index}.title`)} />
                      <Textarea label="Дэд гарчиг" autosize minRows={2} {...form.getInputProps(`heroSlides.${index}.subtitle`)} />
                      <Group grow>
                        <TextInput label="Товчлуурын текст" {...form.getInputProps(`heroSlides.${index}.buttonText`)} />
                        <TextInput label="Товчлуурын линк" {...form.getInputProps(`heroSlides.${index}.buttonLink`)} />
                      </Group>
                      <Text size="sm" fw={500}>Зураг</Text>
                      <ImageUpload
                        value={form.values.heroSlides[index]?.imageUrl}
                        onChange={(url) => form.setFieldValue(`heroSlides.${index}.imageUrl`, url)}
                      />
                    </Stack>
                    <Stack gap={4} mt={28}>
                      <ActionIcon variant="light" disabled={index === 0} onClick={() => moveHeroSlide(index, 'up')}>
                        <IconArrowUp size={16} />
                      </ActionIcon>
                      <ActionIcon variant="light" disabled={index === form.values.heroSlides.length - 1} onClick={() => moveHeroSlide(index, 'down')}>
                        <IconArrowDown size={16} />
                      </ActionIcon>
                      <ActionIcon color="red" variant="light" onClick={() => removeHeroSlide(index)}>
                        <IconTrash size={16} />
                      </ActionIcon>
                    </Stack>
                  </Group>
                </Card>
              ))}
            </Stack>
          )}

          {/* Hero */}
          {activeSection === 'hero' && (
            <Stack gap="sm">
              <TextInput label="Badge текст" {...form.getInputProps('hero.badge')} />
              <TextInput label="Гарчиг" {...form.getInputProps('hero.title')} />
              <Textarea label="Дэд гарчиг" autosize minRows={2} {...form.getInputProps('hero.subtitle')} />
            </Stack>
          )}

          {/* Title + Subtitle sections */}
          {(activeSection === 'featuredSection' || activeSection === 'coursesSection' || activeSection === 'videosSection' || activeSection === 'teachersSection') && (
            <Stack gap="sm">
              <TextInput label="Гарчиг" {...form.getInputProps(`${activeSection}.title`)} />
              <TextInput label="Дэд гарчиг" {...form.getInputProps(`${activeSection}.subtitle`)} />
            </Stack>
          )}

          {/* CTA sections */}
          {(activeSection === 'premiumCta' || activeSection === 'bottomCta') && (
            <Stack gap="sm">
              <TextInput label="Гарчиг" {...form.getInputProps(`${activeSection}.title`)} />
              <Textarea label="Дэд гарчиг" autosize minRows={2} {...form.getInputProps(`${activeSection}.subtitle`)} />
              <TextInput label="Товчлуурын текст" {...form.getInputProps(`${activeSection}.buttonText`)} />
            </Stack>
          )}

          {/* FAQs */}
          {activeSection === 'faqs' && (
            <Stack gap="md">
              <Group justify="space-between">
                <Text fw={600}>FAQ ({form.values.faqs.length})</Text>
                <Button leftSection={<IconPlus size={16} />} variant="light" size="xs" onClick={addFaq}>
                  Нэмэх
                </Button>
              </Group>

              {form.values.faqs.length === 0 && (
                <Text size="sm" c="dimmed" ta="center" py="md">FAQ байхгүй байна</Text>
              )}

              {form.values.faqs.map((_faq, index) => (
                <Box key={index}>
                  {index > 0 && <Divider mb="sm" />}
                  <Group align="flex-start" gap="sm">
                    <Stack gap="xs" style={{ flex: 1 }}>
                      <TextInput label={`Асуулт ${index + 1}`} {...form.getInputProps(`faqs.${index}.question`)} />
                      <Textarea label="Хариулт" autosize minRows={2} {...form.getInputProps(`faqs.${index}.answer`)} />
                    </Stack>
                    <ActionIcon color="red" variant="light" mt={28} onClick={() => removeFaq(index)}>
                      <IconTrash size={16} />
                    </ActionIcon>
                  </Group>
                </Box>
              ))}
            </Stack>
          )}

          <Group justify="flex-end" mt="lg">
            <Button variant="default" onClick={() => setActiveSection(null)}>Буцах</Button>
            <Button onClick={handleSave} loading={loading}>Хадгалах</Button>
          </Group>
        </Card>
      </div>
    )
  }

  // Card grid
  return (
    <div>
      <Title order={2} mb="lg">Нүүр хуудас тохиргоо</Title>

      <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="md">
        {sections.map((s) => (
          <Card
            key={s.key}
            shadow="xs"
            radius="md"
            withBorder
            padding="lg"
            style={{ cursor: 'pointer' }}
            onClick={() => setActiveSection(s.key)}
          >
            <Group gap="md" wrap="nowrap">
              <ActionIcon variant="light" color={s.color} size={48} radius="md">
                <s.icon size={24} />
              </ActionIcon>
              <div>
                <Text fw={600} size="sm">{s.label}</Text>
                <Text size="xs" c="dimmed">{s.description}</Text>
              </div>
            </Group>
          </Card>
        ))}
      </SimpleGrid>
    </div>
  )
}
